// The Director badge count: the caller's open deliveries plus their own open inbox items. One hook
// runs the two polls (both through the one Director loader: the shared ERP refresh tick, visible
// and online only, keeping what was read when a refresh fails) and publishes the count to a small
// module store that every badge reads. The desktop sidebar watcher and the mobile tab-bar source
// both run it; reading the count never polls.
import { useService } from '@toeverything/infra';
import { useEffect, useSyncExternalStore } from 'react';

import { ErpService } from '../../../../../modules/erp';
import type {
  DirectorInboxItem,
  DirectorTaskPage,
} from '../../../../../modules/erp/director-tasks-types';
import { type Deliveries, useDeliveries } from './use-deliveries';
import { type Loaded, useLoader } from './use-loader';

let badgeCount = 0;
const badgeListeners = new Set<() => void>();

function publishBadge(count: number) {
  if (count === badgeCount) return;
  badgeCount = count;
  badgeListeners.forEach(listener => listener());
}

function subscribeBadge(listener: () => void) {
  badgeListeners.add(listener);
  return () => badgeListeners.delete(listener);
}

/** Open deliveries plus the caller's own open inbox items (0 until a poller has read them). */
export function useDirectorBadgeCount(): number {
  return useSyncExternalStore(subscribeBadge, () => badgeCount);
}

export interface DirectorCounts {
  deliveries: Deliveries;
  inbox: Loaded<DirectorTaskPage<DirectorInboxItem>>;
}

/** Polls the caller's deliveries and own inbox and publishes the badge count, only while both
 *  reads stand: a fresh mount never flashes 0 over a count already shown, a failing poll leaves
 *  the previous count, and lost access counts as nothing. */
export function useDirectorCounts(): DirectorCounts {
  const erp = useService(ErpService);
  const deliveries = useDeliveries();
  const inbox = useLoader(
    signal => erp.directorTaskInbox({ scope: 'mine' }, signal),
    [erp]
  );
  const settled = [deliveries.state, inbox.state].every(
    state => state === 'ready' || state === 'forbidden'
  );
  const count = deliveries.items.length + (inbox.data?.items.length ?? 0);
  useEffect(() => {
    if (settled) publishBadge(count);
  }, [count, settled]);
  return { deliveries, inbox };
}
